import type { TIconPack } from "@ch/assets/icons";
import {
  type FC,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";
import type { BaseButtonProps } from "../Buttons/BaseButton";
import type { ButtonProps } from "../Buttons/Button";
import { baseVariants, iconSizes, loaderSizes } from "../Buttons/baseVariants";
import Icon, { type IconProps } from "../Icons/Icon";
import variants from "./variants";

export type TextInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> & {
  leftIcon?: TIconPack;
  rightIcon?: TIconPack;
  leftIconProps?: Omit<IconProps, "name">;
  rightIconProps?: Omit<IconProps, "name">;
  isLoading?: boolean;
  inputStyle?: string;
  shape?: "rounded" | "square" | "pill";
  size?: BaseButtonProps["size"];
  intent?: ButtonProps["intent"] | "underline";
  inputSize?: number; // This is the default size prop for the input
  label?: string;
  labelClassName?: string;
  rightComponent?: ReactNode;
  leftComponent?: ReactNode;
  error?: string;
  helperText?: string;
  helperTextClassName?: string;
  required?: boolean;
};

const TextInput: FC<TextInputProps> = ({
  leftIcon,
  rightIcon,
  leftIconProps,
  rightIconProps,
  isLoading,
  disabled,
  className = "",
  inputStyle = "",
  inputSize,
  shape = "rounded",
  size = "base",
  intent = "solid",
  label,
  labelClassName,
  rightComponent,
  leftComponent,
  error,
  helperText,
  helperTextClassName,
  required,
  ...props
}) => {
  const inputId = useId();

  const shapeClassName = baseVariants.shape[shape];
  const sizeClassName = baseVariants.size[size];
  const inputClassName = variants[intent];
  const errorClassName = error ? "!border-orange" : "";
  const disabledClassName = disabled ? "opacity-50" : "";
  const loaderSizeClassName = loaderSizes[size];

  const iconSize = iconSizes[size];

  return (
    <div>
      <div className="flex flex-col items-stretch gap-2">
        {label && (
          <label
            className={`label-text text-sm text-gray-extras-100 ${labelClassName}`}
            htmlFor={inputId}
          >
            {label}
            {required && <span className="text-sm text-red-400"> *</span>}
          </label>
        )}
        <div
          className={`flex items-center gap-3 transition-all ${shapeClassName} ${sizeClassName} ${inputClassName} ${errorClassName} ${disabledClassName} ${className}`}
        >
          {leftIcon && (
            <Icon name={leftIcon} size={iconSize} {...leftIconProps} />
          )}

          {leftComponent}

          <input
            className={`h-full w-full flex-1 bg-transparent focus:outline-none ${inputStyle}`}
            disabled={disabled}
            id={inputId}
            size={inputSize}
            type="text"
            {...props}
          />

          {isLoading && (
            <span
              aria-label="Loading"
              className={`loading loading-dots ${loaderSizeClassName}`}
            />
          )}

          {rightComponent}

          {rightIcon && (
            <Icon name={rightIcon} size={iconSize} {...rightIconProps} />
          )}
        </div>
      </div>
      {error || helperText ? (
        <span
          className={`text-xs ${
            error ? "text-orange" : "text-gray-400"
          } ${helperTextClassName}`}
        >
          {error ?? helperText}
        </span>
      ) : null}
    </div>
  );
};

export default TextInput;
